/**
 * 확장 기여 표 — 타입이 붙은 단일 원천 (ADR 0024)
 * --------------------------------------------------------------------------
 * 생성된 매니페스트(`@/generated/extensions`)를 호스트가 쓸 수 있는 **기여 목록**으로
 * 바꿉니다. 확장을 더할 때 코어 표를 손대지 않아도 되는 이유가 이 파일입니다 —
 * 라우트·nav 사실이 여기서 나와 ADR 0023 레지스트리로 합쳐집니다.
 *
 * 이 파일은 **leaf 모듈**입니다. 화면·API·React를 import하지 않으므로 라우트
 * 레지스트리가 순환 없이 읽을 수 있습니다.
 *
 * 다섯 가지 기여를 모두 같은 규칙으로 노출합니다.
 *   Route · Nav · ResourceDetailSection · DashboardWidget · Command
 * 순서는 결정적이고(확장 순서 = 의존성 위상, 그 안은 선언 순서), 개수는 매니페스트
 * 검증이 이미 상한을 강제했으므로 유계입니다.
 */
import type {
  ExtensionCommand,
  ExtensionManifest,
  ExtensionNavEntry,
  ExtensionNavGroup,
  ExtensionRoute,
  ExtensionRouteCapability,
  ExtensionSection,
  ExtensionSectionTarget,
  ExtensionWidget,
} from "@k8s-dashboard/extensions";
import { extensionManifests } from "@/generated/extensions";

/** 이 빌드에 들어간 확장 전부. 순서는 코드 생성이 정한 결정적 순서입니다. */
export const EXTENSION_MANIFESTS: readonly ExtensionManifest[] = extensionManifests;

/** 기여는 항상 **누가 준 것인지**를 달고 다닙니다. 소유자 없이 떠도는 기여는 없습니다. */
export type Owned<T> = T & { readonly extensionId: string };

const NAV_GROUP_ORDER: readonly ExtensionNavGroup[] = ["observe", "resources", "manage", "custom"];

/**
 * 복합 키 구분자. 확장 id·라우트 id는 `[a-z0-9-]`만 쓰므로 이 문자가 값에 들어갈 수
 * 없고, 그래서 "a"+"bc"와 "ab"+"c"가 같은 키가 되는 사고가 원천적으로 생기지 않습니다.
 * 소스에는 **보이는 이스케이프**로 적습니다 — 원시 제어 바이트를 파일에 넣지 않습니다.
 */
const KEY_SEPARATOR = "\u0000";

function collect<T>(manifests: readonly ExtensionManifest[], pick: (m: ExtensionManifest) => readonly T[]): Owned<T>[] {
  const out: Owned<T>[] = [];
  for (const manifest of manifests) for (const value of pick(manifest)) out.push({ ...value, extensionId: manifest.id });
  return out;
}

export function routeContributions(manifests: readonly ExtensionManifest[] = EXTENSION_MANIFESTS): Owned<ExtensionRoute>[] {
  return collect(manifests, (m) => m.routes);
}

/** nav는 그룹 순서 → 선언된 order 순입니다. 좌측 nav와 팔레트가 같은 순서를 봅니다. */
export function navContributions(manifests: readonly ExtensionManifest[] = EXTENSION_MANIFESTS): Owned<ExtensionNavEntry>[] {
  return collect(manifests, (m) => m.nav).sort(
    (a, b) => NAV_GROUP_ORDER.indexOf(a.group) - NAV_GROUP_ORDER.indexOf(b.group) || a.order - b.order,
  );
}

/** 리소스 상세 섹션. target을 주면 그 상세 화면의 것만 돌려줍니다. */
export function sectionContributions(
  target?: ExtensionSectionTarget,
  manifests: readonly ExtensionManifest[] = EXTENSION_MANIFESTS,
): Owned<ExtensionSection>[] {
  const all = collect(manifests, (m) => m.sections);
  return target ? all.filter((section) => section.target === target) : all;
}

export function widgetContributions(manifests: readonly ExtensionManifest[] = EXTENSION_MANIFESTS): Owned<ExtensionWidget>[] {
  return collect(manifests, (m) => m.widgets);
}

export function commandContributions(manifests: readonly ExtensionManifest[] = EXTENSION_MANIFESTS): Owned<ExtensionCommand>[] {
  return collect(manifests, (m) => m.commands);
}

/**
 * 활성 확장의 기여만 남깁니다. 서버 능력이 확인되지 않은 확장의 기여는 **보이지
 * 않습니다** — 목록에서 지우는 것이 "눌렀더니 안 되는 항목"보다 정직합니다.
 */
export function ownedByEnabled<T>(contributions: readonly Owned<T>[], enabled: ReadonlySet<string>): Owned<T>[] {
  return contributions.filter((contribution) => enabled.has(contribution.extensionId));
}

/** 라우트 레지스트리(ADR 0023)가 그대로 쓸 수 있는 형태의 확장 라우트 사실. */
export interface ExtensionRouteMeta {
  id: string;
  path: string;
  label: string;
  group: ExtensionNavGroup;
  capability: ExtensionRouteCapability;
  nav: boolean;
  /** 코어 라우트 사이의 삽입 위치. nav가 없는 라우트는 undefined이고 뒤에 붙습니다. */
  order?: number;
  extensionId: string;
}

/**
 * 확장 라우트를 레지스트리 항목으로 변환합니다.
 *
 * 라벨·그룹·순서는 nav 기여에서 옵니다. nav 기여가 없는 라우트는 목적지 목록에
 * 나타나지 않는 상세 화면이므로 확장 제목을 라벨로 씁니다.
 */
export function extensionRouteMetas(manifests: readonly ExtensionManifest[] = EXTENSION_MANIFESTS): ExtensionRouteMeta[] {
  const navByRoute = new Map(
    navContributions(manifests).map((entry) => [entry.extensionId + KEY_SEPARATOR + entry.routeId, entry]),
  );
  const titles = new Map(manifests.map((manifest) => [manifest.id, manifest.title]));
  const metas = routeContributions(manifests).map((route): ExtensionRouteMeta => {
    const nav = navByRoute.get(route.extensionId + KEY_SEPARATOR + route.id);
    return {
      id: route.id,
      path: route.path,
      label: nav?.label ?? titles.get(route.extensionId) ?? route.id,
      group: nav?.group ?? "custom",
      capability: route.capability,
      nav: nav !== undefined,
      ...(nav ? { order: nav.order } : {}),
      extensionId: route.extensionId,
    };
  });
  /* nav 있는 것 먼저(선언된 order), 없는 것은 선언 순서대로 뒤에. */
  return metas.sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER));
}

/** 코어 라우트에 부여하는 암묵 순서 간격. 확장은 이 사이의 값을 골라 위치를 정합니다. */
export const CORE_ORDER_STEP = 10;

/**
 * 코어 라우트 표와 확장 라우트를 하나의 결정적 순서로 합칩니다.
 *
 * 코어 항목은 선언 순서대로 0, 10, 20…의 암묵 순서를 갖고, 확장은 매니페스트가
 * 선언한 order로 그 사이에 들어갑니다. 정렬은 안정적이라 같은 order면 코어가 앞섭니다.
 */
export function mergeRouteMeta<T extends { id: string }>(core: readonly T[], extensions: readonly (T & { order?: number })[]): T[] {
  const ranked = [
    ...core.map((meta, index) => ({ meta, order: index * CORE_ORDER_STEP, tie: 0 })),
    ...extensions.map((meta, index) => ({ meta, order: meta.order ?? Number.MAX_SAFE_INTEGER, tie: 1 + index })),
  ];
  ranked.sort((a, b) => a.order - b.order || a.tie - b.tie);
  return ranked.map((entry) => entry.meta);
}
